import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { labManagerApi, parseApiError } from '../services/apiClient';
import { requirePaginatedResponse } from '../services/responseValidation';
import type { PaginatedResponse } from '../types';
import StudentTerminal from '../components/StudentTerminal';
import { useStudentTerminal } from '../hooks/useStudentTerminal';
import styles from './Environments.module.css';

interface EnvironmentRecord {
  id: string;
  userId: string;
  studentName: string;
  studentEmail: string;
  labId: string;
  labTitle: string;
  status: string;
  createdAt: string;
  startedAt?: string;
  lastActivityAt?: string;
}

const Environments: React.FC = () => {
  const [searchParams] = useSearchParams();
  const studentId = searchParams.get('studentId') || undefined;
  const [status, setStatus] = useState('all');
  const [page, setPage] = useState(1);
  const terminal = useStudentTerminal();
  const environments = useQuery({
    queryKey: ['environments', 'scoped', { userId: studentId, status, page }],
    queryFn: async () =>
      requirePaginatedResponse<EnvironmentRecord>(
        (
          await labManagerApi.get<PaginatedResponse<EnvironmentRecord>>(
            '/instructor/environments',
            {
              params: {
                userId: studentId,
                status: status === 'all' ? undefined : status,
                page,
                limit: 25,
              },
            },
          )
        ).data,
        'environments',
        [
          'id',
          'userId',
          'studentName',
          'studentEmail',
          'labId',
          'labTitle',
          'status',
          'createdAt',
        ],
      ),
    refetchInterval: 30000,
  });
  return (
    <div className={styles.container} style={{ padding: 32 }}>
      <header className={styles.header}>
        <div>
          <h1>Student environments</h1>
          <p>
            Monitor lab sessions and connect to running environments within your
            teaching scope. Learners start and reset their sessions from the lab
            workspace.
          </p>
          {studentId && (
            <Link to={`/students/${encodeURIComponent(studentId)}`}>
              Back to selected student
            </Link>
          )}
        </div>
        <button
          type="button"
          className={styles.filterBtn}
          disabled={environments.isFetching}
          onClick={() => environments.refetch()}
        >
          {environments.isFetching ? 'Refreshing…' : 'Refresh'}
        </button>
      </header>
      <div className={styles.filters}>
        <label htmlFor="environment-status">Status</label>
        <select
          id="environment-status"
          value={status}
          onChange={(event) => {
            setStatus(event.target.value);
            setPage(1);
          }}
        >
          {[
            'all',
            'running',
            'pending',
            'provisioning',
            'stopped',
            'error',
            'terminated',
          ].map((value) => (
            <option key={value} value={value}>
              {value === 'all' ? 'All statuses' : value}
            </option>
          ))}
        </select>
        <span>
          {environments.data
            ? `${environments.data.pagination.total} matching environments`
            : ''}
        </span>
      </div>
      {environments.isLoading ? (
        <p role="status" className={styles.loading}>
          Loading environments…
        </p>
      ) : environments.isError ? (
        <div role="alert" className={styles.loading}>
          <h2>Couldn&apos;t load environments</h2>
          <p>
            {parseApiError(environments.error).status === 403
              ? 'Your account does not have access to these lab sessions.'
              : 'Environment monitoring is unavailable. Try again.'}
          </p>
          <button
            type="button"
            className={styles.filterBtn}
            onClick={() => environments.refetch()}
          >
            Retry
          </button>
        </div>
      ) : !environments.data?.data.length ? (
        <div className={styles.empty}>
          <h2>No matching environments</h2>
          <p>
            {status === 'all'
              ? 'Student lab sessions will appear here when they are provisioned.'
              : 'Try a different status filter.'}
          </p>
        </div>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table className={styles.table} style={{ minWidth: 680 }}>
              <thead>
                <tr>
                  <th scope="col">Student</th>
                  <th scope="col">Lab</th>
                  <th scope="col">Status</th>
                  <th scope="col">Created</th>
                  <th scope="col">Environment</th>
                  <th scope="col">Support</th>
                </tr>
              </thead>
              <tbody>
                {environments.data.data.map((environment) => (
                  <tr key={environment.id}>
                    <td>
                      <Link
                        to={`/students/${encodeURIComponent(environment.userId)}`}
                      >
                        {environment.studentName || environment.studentEmail}
                      </Link>
                      <p>{environment.studentEmail}</p>
                    </td>
                    <td>{environment.labTitle || environment.labId}</td>
                    <td>{environment.status}</td>
                    <td>
                      {Number.isFinite(
                        new Date(environment.createdAt).getTime(),
                      )
                        ? formatDistanceToNow(new Date(environment.createdAt), {
                            addSuffix: true,
                          })
                        : '—'}
                    </td>
                    <td>
                      <code>{environment.id}</code>
                    </td>
                    <td>
                      {environment.status === 'running' ? (
                        <button
                          type="button"
                          className={styles.filterBtn}
                          onClick={() =>
                            terminal.open({
                              environmentId: environment.id,
                              studentName: environment.studentName,
                              studentEmail: environment.studentEmail,
                            })
                          }
                        >
                          Connect terminal
                        </button>
                      ) : (
                        'Available when running'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <nav className={styles.filters} aria-label="Environment pages">
            <button
              type="button"
              className={styles.filterBtn}
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </button>
            <span>
              Page {page} of {environments.data.pagination.totalPages}
            </span>
            <button
              type="button"
              className={styles.filterBtn}
              disabled={page >= environments.data.pagination.totalPages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </button>
          </nav>
        </>
      )}
      {terminal.isOpen && terminal.environmentId && terminal.studentInfo && (
        <StudentTerminal
          key={terminal.environmentId}
          environmentId={terminal.environmentId}
          studentName={terminal.studentInfo.name}
          studentEmail={terminal.studentInfo.email}
          onClose={terminal.close}
        />
      )}
    </div>
  );
};

export default Environments;
